/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from 'react-bootstrap'
import DatePickerComponent from '@mdsol/lego/lib/DatePicker'
import Modal from '../Modal'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import { useEsignature } from '../../hooks/useEsignature'
import { confirmModal } from '../../utils/confirmModal'
import { getMomentDateFormat, getDatePickerParseFormats } from '~/utils/dateFormatting'

const DatePicker = DatePickerComponent as any

/**
 * Set Due Date Modal Component
 *
 * Allows users to set or clear due dates for selected files/placeholders.
 * Supports batch operations for:
 * - Multiple files (standard) via /multiSetDueDate
 * - Property search results (batch) via /propertysearch/setduedateforall
 * - Folder-wide changes (batch) via /{folderId}/setduedateforall
 *
 * Features:
 * - Date selection with DatePicker
 * - E-signature validation support
 * - Integration with file update events
 * - Cache invalidation for updated files
 */

interface SelectedFile {
  id: number
}

interface SetDueDateModalProps {
  options: {
    selectedIds: number[]
    selectedWholeResult?: boolean
    selectedItems?: SelectedFile[]
    parentFolderId?: number
    isPropertiesSearch?: boolean
    filter?: any
    meta?: any
    [key: string]: unknown
  }
  onClose?: () => void
}

interface FormValues {
  dueDate: any
}

export const SetDueDateModal: React.FC<SetDueDateModalProps> = ({ options, onClose }) => {
  const { t: translate } = useTranslation()
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { api } = context
  const { signAction } = useEsignature()
  const { hideModal } = useModal()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const defaultFilterConst: FormValues = { dueDate: null }

  useEffect(() => {
    setIsOpen(true)
  }, [])

  const doClose = () => {
    setIsOpen(false)
    hideModal()
    if (onClose) {
      onClose()
    }
  }

  const confirmPastDate = (dueDateTimestamp: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (dueDateTimestamp < today.getTime()) {
        confirmModal({
          titleMsg: translate('CONFIRMATION'),
          msg: translate('FSI_DETAILS_SAVE_DUE_DATE_PAST_CONFIRM_QUESTION'),
          cancelTxt: translate('NO'),
          okTxt: translate('YES'),
          onSubmit: resolve,
          onCancel: reject
        })
      } else {
        resolve()
      }
    })
  }

  const save = async (values: FormValues) => {
    // Convert date to Unix timestamp at midnight first (needed for past-date check)
    let dueDateTimestamp: number | null = null
    if (values.dueDate) {
      const date = new Date(values.dueDate)
      date.setHours(0, 0, 0, 0)
      dueDateTimestamp = date.getTime()
    }

    // If a past date was selected, ask for confirmation before proceeding
    if (dueDateTimestamp !== null) {
      try {
        await confirmPastDate(dueDateTimestamp)
      } catch {
        // User clicked NO - abort save
        return
      }
    }

    // Close modal immediately (don't wait for API)
    doClose()

    const doSetDueDateCallback = async (validation?: Record<string, unknown>) => {
      try {
        await api.files.setDueDateFiles(options.selectedIds, dueDateTimestamp, validation, options)

        // Emit global events for backward compatibility (window event bus)
        window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateDashboard', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateSearchFiles'))

        // Invalidate React Query caches
        queryClient.invalidateQueries({ queryKey: ['files'] })
        queryClient.invalidateQueries({ queryKey: ['documents'] })
        queryClient.invalidateQueries({ queryKey: ['dashboard'] })

        toast.success(translate('BROWSER_SETTINGS_SET_DUE_DATE_SUCCESS_MSG'))
      } catch (error: any) {
        const errorMessage = error?.data?.message || error?.message || 'Unknown error'
        toast.error(translate('ERROR_ERROR', { error: errorMessage }))
      }
    }

    try {
      // Check if e-signature is required
      const requiresEsig = auth.requireESignatureForFileCommands()

      if (requiresEsig) {
        try {
          const validation = await signAction('ADMIN_ACTION', 'ADMIN_ACTION_SET_FSI_DUE_DATE')
          await doSetDueDateCallback(validation as Record<string, unknown>)
        } catch {
          // E-signature cancelled or failed - do nothing
        }
      } else {
        await doSetDueDateCallback()
      }
    } catch (error) {
      toast.error(translate('ERROR_ERROR', { error: String(error) }))
    }
  }

  return (
    <Formik<FormValues> enableReinitialize={true} initialValues={defaultFilterConst} onSubmit={save}>
      {({ values, setFieldValue, handleSubmit }) => (
        <Modal
          show={isOpen}
          handleClose={doClose}
          title={translate('BROWSER_SETTINGS_SET_DUE_DATE_H_TITLE')}
          className="theme-sandman"
          style={{ overflow: 'visible' }}
          size="md"
        >
          <Modal.Body>
            <div>
              <div className="form-group w50p">
                <label className="control-label" htmlFor="due-date-picker">
                  {translate('BROWSER_SETTINGS_SET_DUE_DATE_LABEL')}
                </label>
                <DatePicker
                  id="due-date-picker"
                  name="dueDate"
                  selected={values.dueDate}
                  onChange={(val: any) => {
                    setFieldValue('dueDate', val)
                  }}
                  popoverTargetOffset="3px 0"
                  todayButton="Today"
                  dateFormatCalendar="MMMM"
                  dateFormat={getDatePickerParseFormats()}
                  showYearDropdown
                  className="form-control field-control w100p"
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="text-right">
              <Button className="btn btn-chita btn-close" data-action="cancel" onClick={doClose}>
                {translate('CANCEL')}
              </Button>
              <Button data-action="confirm" className="btn btn-primary text-capitalize" onClick={() => handleSubmit()}>
                {translate('OK')}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}

export default SetDueDateModal
